import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { freeze } from 'immer';
import type { ActiveOrderCardsState, CustomerOrderCardData, OrderCardData, RFQCardData } from './types';

export const initialState: ActiveOrderCardsState = {
  clearedIDs: new Set(),
  openOrdersData: new Map(),
  filledRFQsData: new Map(),
  customerOrderCardsData: new Map(),
  expandedCardID: undefined,
  // openOrdersData contains the details for the cards pertaining to current user
  // however in order to have the ability to modify on behalf of other users, we still need to load in other user's card data on demand
  // so that when a user initiate modify workflow we can render the card on top of the order form
  otherOrdersData: new Map(),
};

const activeOrderCardsSlice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    setOrderCardsData: (state, action: PayloadAction<Map<string, OrderCardData>>) => {
      state.openOrdersData = freeze(action.payload);
    },
    addOtherOrderCardsData: (state, action: PayloadAction<OrderCardData>) => {
      const key = action.payload.order.OrderID;
      const showDetails = state.otherOrdersData.get(key)?.showDetails || false;
      state.otherOrdersData.set(action.payload.order.OrderID, {
        ...action.payload,
        showDetails,
      });
    },
    setRFQCardsData: (state, action: PayloadAction<Map<string, RFQCardData>>) => {
      state.filledRFQsData = action.payload;
    },
    addCustomerOrderCardData: (state, action: PayloadAction<CustomerOrderCardData>) => {
      state.customerOrderCardsData.set(action.payload.customerOrder.OrderID, action.payload);
    },
    toggleShowDetails: (state, action: PayloadAction<string>) => {
      state.expandedCardID = state.expandedCardID === action.payload ? undefined : action.payload;
    },
    clearID: (state, action: PayloadAction<string>) => {
      state.clearedIDs = state.clearedIDs.add(action.payload);
    },
    clearIDs: (state, action: PayloadAction<string[]>) => {
      for (const id of action.payload) {
        state.clearedIDs = state.clearedIDs.add(id);
      }
    },
  },
});

export const {
  setOrderCardsData,
  setRFQCardsData,
  addCustomerOrderCardData,
  clearID,
  clearIDs,
  toggleShowDetails,
  addOtherOrderCardsData,
} = activeOrderCardsSlice.actions;

export const selectVisibleOrdersData = createSelector(
  (state: AppState) => state.cards.openOrdersData,
  (state: AppState) => state.cards.clearedIDs,
  (state: AppState) => state.cards.expandedCardID,
  (openOrdersData, clearedIDs, expandedCardID) => {
    return Array.from(openOrdersData.values())
      .filter(data => !clearedIDs.has(data.order.OrderID))
      .map(data => {
        if (data.order.OrderID === expandedCardID) {
          return { ...data, showDetails: true };
        }
        return data;
      });
  }
);

/** Select card based on OrderID matching OrderID or ParentOrderID (for CustomerOrders) */
export const selectOrderCardData = (orderID: string | undefined) => {
  return createSelector(selectVisibleOrdersData, ordersData => {
    return orderID != null
      ? ordersData.find(data => data.order.OrderID === orderID || data.order.ParentOrderID === orderID)
      : undefined;
  });
};

export const selectVisibleRFQsData = createSelector(
  (state: AppState) => state.cards.filledRFQsData,
  (state: AppState) => state.cards.clearedIDs,
  (state: AppState) => state.cards.expandedCardID,
  (filledRFQsData, clearedIDs, expandedCardID) => {
    return Array.from(filledRFQsData.values())
      .filter(data => !clearedIDs.has(data.quote.RFQID))
      .map(data => {
        if (data.quote.RFQID === expandedCardID) {
          return { ...data, showDetails: true };
        }
        return data;
      });
  }
);

/** Select card based on RFQID matching quote.RFQID or customerQuote.RFQID */
export const selectRFQCardData = (rfqID: string | undefined) => {
  return createSelector(selectVisibleRFQsData, rfqsData => {
    return rfqID != null
      ? rfqsData.find(data => data.quote.RFQID === rfqID || data.customerQuote?.RFQID === rfqID)
      : undefined;
  });
};

export const selectVisibleCustomerOrderCardsData = createSelector(
  (state: AppState) => state.cards.customerOrderCardsData,
  (state: AppState) => state.cards.clearedIDs,
  (customerOrderCardsData, clearedIDs) => {
    return Array.from(customerOrderCardsData.values()).filter(data => !clearedIDs.has(data.customerOrder.OrderID));
  }
);

export const activeOrderCardsSliceReducer = activeOrderCardsSlice.reducer;
